import type { ReactNode } from "react";

import { cx } from "@/utils/cx";

export interface Column<T> {
	className?: string;
	header: string;
	key: string;
	render: (row: T) => ReactNode;
}

interface DataTableProps<T> {
	columns: Column<T>[];
	onRowClick?: (row: T) => void;
	rowKey: (row: T) => string;
	rows: T[];
}

/** Plain, dense table in the app's existing style. No sorting, no virtualisation. */
export function DataTable<T>({
	columns,
	onRowClick,
	rowKey,
	rows,
}: DataTableProps<T>) {
	return (
		<div className="overflow-x-auto">
			<table className="w-full text-left text-sm">
				<thead className="text-tertiary border-secondary border-b">
					<tr>
						{columns.map((column) => (
							<th
								className={cx("py-2 pr-4 font-medium", column.className)}
								key={column.key}
								scope="col"
							>
								{column.header}
							</th>
						))}
					</tr>
				</thead>
				<tbody>
					{rows.map((row) => (
						<tr
							className={cx(
								"border-secondary text-primary border-b align-top",
								onRowClick && "hover:bg-brand-50 cursor-pointer",
							)}
							key={rowKey(row)}
							onClick={onRowClick ? () => onRowClick(row) : undefined}
						>
							{columns.map((column) => (
								<td
									className={cx("py-2 pr-4", column.className)}
									key={column.key}
								>
									{column.render(row)}
								</td>
							))}
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}
